/**
 * PaymentSuccess - Página mostrada después de un pago exitoso
 *
 * Muestra un mensaje contextual (suscripción Pro, guía individual o
 * pack) en base a los query params que el backend añade al success_url
 * de Stripe: ?type=COMPLETE|GUIDE|PACK&pack_id=...&guide_id=...
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFirebase } from "../context/ApiContext";
import PageHeadNoIndex from "../components/Seo/PageHeadNoIndex";
import "./PaymentResult.css";

const PaymentSuccess = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { refreshSubscription, refreshEntitlements, getPacks } = useFirebase();
	const [loading, setLoading] = useState(true);
	const [packInfo, setPackInfo] = useState(null);

	const purchaseType = (searchParams.get("type") || "").toUpperCase();
	const packId = searchParams.get("pack_id");
	const guideId = searchParams.get("guide_id");

	const fnRef = useRef({ refreshSubscription, refreshEntitlements, getPacks });
	fnRef.current = { refreshSubscription, refreshEntitlements, getPacks };

	useEffect(() => {
		let cancelled = false;
		const handleSuccess = async () => {
			const {
				refreshSubscription: rs,
				refreshEntitlements: re,
				getPacks: gp,
			} = fnRef.current;

			try {
				await Promise.all([rs?.(), re?.()]);
			} catch (error) {
				console.error("Error refreshing after payment:", error);
			}

			if (purchaseType === "PACK" && packId && gp) {
				try {
					const list = await gp();
					const found = (list || []).find((p) => p.id === packId);
					if (!cancelled && found) setPackInfo(found);
				} catch (error) {
					console.error("Error loading pack info:", error);
				}
			}

			if (!cancelled) setLoading(false);
		};

		const timer = setTimeout(handleSuccess, 2000);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [purchaseType, packId]);

	const content = useMemo(() => {
		if (purchaseType === "PACK") {
			const title = packInfo?.title || "¡Pack activado!";
			const flag = packInfo?.marketing?.flag || "";
			const highlights = packInfo?.marketing?.highlights || [];
			return {
				variant: "pack",
				emoji: flag,
				heading: title,
				message:
					packInfo?.subtitle ||
					"Ya tienes acceso a todo el contenido del pack.",
				highlights,
				primaryLabel: "Abrir la guía",
				primaryOnClick: () => {
					const firstGuide = packInfo?.entitlements?.prebuiltGuides?.[0];
					if (firstGuide)
						navigate(`/preview/prebuilt/${firstGuide}`, {
							state: { from: "/mis-guias" },
						});
					else navigate("/mis-guias");
				},
				secondaryLabel: "Ir al dashboard",
				secondaryOnClick: () => navigate("/"),
			};
		}
		if (purchaseType === "GUIDE") {
			return {
				variant: "guide",
				emoji: "📖",
				heading: "¡Guía desbloqueada!",
				message:
					"Tu compra se ha completado. Ya puedes abrir y editar la guía cuando quieras desde tu dashboard.",
				highlights: [],
				primaryLabel: guideId ? "Abrir la guía" : "Ir a mis guías",
				primaryOnClick: () =>
					guideId
						? navigate(`/preview/prebuilt/${guideId}`, {
								state: { from: "/mis-guias" },
							})
						: navigate("/mis-guias"),
				secondaryLabel: "Explorar más guías",
				secondaryOnClick: () => navigate("/"),
			};
		}
		if (purchaseType === "COMPLETE") {
			return {
				variant: "pro",
				emoji: "✨",
				heading: "¡Bienvenida al plan Pro!",
				message:
					"Tienes acceso ilimitado a todas las guías, al editor y a los extras premium.",
				highlights: [],
				primaryLabel: "Explorar guías",
				primaryOnClick: () => navigate("/"),
				secondaryLabel: "Ir a mis guías",
				secondaryOnClick: () => navigate("/mis-guias"),
			};
		}
		return {
			variant: "generic",
			emoji: null,
			heading: "¡Pago completado!",
			message:
				"Tu pago se ha procesado correctamente. Ya puedes disfrutar del contenido que has desbloqueado.",
			highlights: [],
			primaryLabel: "Explorar guías",
			primaryOnClick: () => navigate("/"),
			secondaryLabel: "Ir a mis guías",
			secondaryOnClick: () => navigate("/mis-guias"),
		};
	}, [purchaseType, packInfo, guideId, navigate]);

	return (
		<>
			<PageHeadNoIndex
				title="Pago completado"
				description="Confirmación de compra en Viajes Molones. Accede a tus guías y beneficios desbloqueados."
			/>
			<div className="payment-result-page">
			<div className="payment-result-card success">
				<div className="payment-result-icon">
					{content.emoji ? (
						<div
							style={{
								fontSize: 64,
								lineHeight: 1,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							{content.emoji}
						</div>
					) : (
						<svg
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<circle cx="12" cy="12" r="10" fill="#10B981" />
							<path
								d="M8 12L11 15L16 9"
								stroke="white"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					)}
				</div>

				<h1>{content.heading}</h1>

				{loading ? (
					<p className="payment-result-message">Procesando tu pago...</p>
				) : (
					<>
						<p className="payment-result-message">{content.message}</p>

						{content.highlights.length > 0 && (
							<ul
								style={{
									listStyle: "none",
									padding: 0,
									margin: "0 0 24px",
									display: "flex",
									flexDirection: "column",
									gap: 8,
									textAlign: "left",
								}}
							>
								{content.highlights.map((h, idx) => (
									<li
										key={idx}
										style={{
											display: "flex",
											alignItems: "flex-start",
											gap: 10,
											padding: "10px 14px",
											borderRadius: 10,
											background: "#f8fafc",
											border: "1px solid #e2e8f0",
										}}
									>
										<span
											style={{
												color: "#10B981",
												fontWeight: 700,
												fontSize: 16,
												lineHeight: "20px",
											}}
										>
											✓
										</span>
										<div style={{ minWidth: 0 }}>
											<div
												style={{
													fontWeight: 600,
													color: "#0f172a",
													fontSize: 14,
													lineHeight: 1.3,
												}}
											>
												{h.title}
											</div>
											{h.description && (
												<div
													style={{
														color: "#64748b",
														fontSize: 12,
														lineHeight: 1.4,
														marginTop: 2,
													}}
												>
													{h.description}
												</div>
											)}
										</div>
									</li>
								))}
							</ul>
						)}

						<div className="payment-result-actions">
							<button
								className="payment-button primary"
								onClick={content.primaryOnClick}
							>
								{content.primaryLabel}
							</button>
							<button
								className="payment-button secondary"
								onClick={content.secondaryOnClick}
							>
								{content.secondaryLabel}
							</button>
						</div>
					</>
				)}
			</div>
		</div>
		</>
	);
};

export default PaymentSuccess;
